import {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { matchesQuery } from "../lib/foldText";

export type SelectOption<T extends string = string> = {
  value: T;
  label: string;
  /** Optional leading node (flag, icon, color dot). */
  left?: ReactNode;
  /**
   * Optional heading this option sits under, the <optgroup> equivalent. Options
   * carrying the same group render beneath one header, in list order; options
   * without a group render ungrouped above the first header.
   */
  group?: string;
  /** Extra text the filter also matches (code, path…) besides `label`. */
  keywords?: string;
};

type Props<T extends string> = {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Applied to the wrapper — use for width / margins. */
  style?: CSSProperties;
};

const PANEL_MAX_HEIGHT = 360;
const PANEL_GAP = 4;
const ROW_H = 36;
const SEARCH_H = 44;

/**
 * App-styled replacement for native <select>. The closed control looks like an
 * `.input`; the open list is a custom popover (var(--surface)/var(--border)) so
 * it matches the rest of the UI instead of the OS dropdown. Keyboard + outside
 * click supported. Use this for every fixed-option dropdown going forward.
 *
 * Every list is searchable (Strahinja, 2026-09-10: "svi dropdowni da mogu i da
 * se kucaju"): the open panel starts with a focused filter box; typing on the
 * closed trigger opens the list with that text already in the box. Matching is
 * diacritics-insensitive over `label` + `keywords`.
 *
 * The list is rendered in a portal with fixed positioning so a scrollable
 * table wrapper, card or modal (overflow: auto/hidden) can never clip it; it
 * flips above the trigger when there is no room below.
 */
export function Select<T extends string>({
  value, options, onChange, disabled, placeholder, ariaLabel, style,
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selected = options.find((o) => o.value === value) ?? null;

  const visible = useMemo(
    () => (query ? options.filter((o) => matchesQuery(`${o.label} ${o.keywords ?? ""}`, query)) : options),
    [options, query],
  );

  function openList(initialQuery = "") {
    if (disabled) return;
    setQuery(initialQuery);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setQuery("");
  }

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Focus the filter box and highlight the current value each time the list opens.
  useEffect(() => {
    if (!open) return;
    const i = options.findIndex((o) => o.value === value);
    setActive(i >= 0 ? i : 0);
    const id = window.requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Typing narrows the list: the highlight goes back to the first match.
  useEffect(() => {
    if (open) setActive(0);
  }, [query, open]);

  // Anchor the fixed panel to the trigger; follow scroll/resize of any ancestor.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place() {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // The list is a fixed portal: it may run past a modal's edge on purpose (Strahinja:
      // a long list is better than a short scrolling one); only the viewport limits it.
      const below = viewportH - r.bottom - PANEL_GAP;
      const above = r.top - PANEL_GAP;
      const wanted = Math.min(PANEL_MAX_HEIGHT, SEARCH_H + Math.max(options.length, 1) * ROW_H + 8);
      const flip = below < wanted && above > below;
      const maxHeight = Math.max(160, Math.min(PANEL_MAX_HEIGHT, flip ? above : below));
      // At least as wide as the trigger; may grow for long labels (one line each) up to the viewport edge.
      const maxWidth = Math.max(r.width, Math.min(560, window.innerWidth - r.left - 8));
      setPos(flip
        ? { position: "fixed", left: r.left, minWidth: r.width, maxWidth, bottom: viewportH - r.top + PANEL_GAP, maxHeight }
        : { position: "fixed", left: r.left, minWidth: r.width, maxWidth, top: r.bottom + PANEL_GAP, maxHeight });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, options.length]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    const row = panelRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    row?.scrollIntoView?.({ block: "nearest" });
  }, [active, open]);

  function choose(i: number) {
    const opt = visible[i];
    if (!opt) return;
    onChange(opt.value);
    close();
    wrapRef.current?.querySelector("button")?.focus({ preventScroll: true });
  }

  function onTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    switch (e.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        openList();
        break;
      default:
        // A printable character starts the search straight away.
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          openList(e.key);
        }
    }
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        choose(active);
        break;
      case "Escape":
        e.preventDefault();
        close();
        wrapRef.current?.querySelector("button")?.focus({ preventScroll: true });
        break;
      case "Tab":
        close();
        break;
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => Math.min(visible.length - 1, a + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(Math.max(0, visible.length - 1));
        break;
    }
  }

  const list = open && pos && (
    <div ref={panelRef} className="dd-panel" style={{ ...panel, ...pos }}>
      <div className="dd-search">
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden style={{ flex: "none", color: "var(--ink-3)" }}>
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M20 20l-3.5-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={t("select.search", "Kucaj za pretragu…")}
          aria-label={t("select.search", "Kucaj za pretragu…")}
          aria-controls="dd-listbox"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div id="dd-listbox" role="listbox" className="dd-scroll" style={listStyle}>
        {visible.length === 0 && <div className="dd-empty">{t("select.empty", "Nema rezultata.")}</div>}
        {visible.map((o, i) => {
          const isSel = o.value === value;
          // First option of a new group gets the header above it.
          const header = o.group && o.group !== visible[i - 1]?.group ? o.group : null;
          return (
            <div key={o.value}>
              {header && <div style={groupHead}>{header}</div>}
              <button
                type="button"
                role="option"
                data-index={i}
                aria-selected={isSel}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
                style={{ ...option, background: i === active ? "var(--surface-hover)" : "transparent", fontWeight: isSel ? 600 : 400 }}
              >
                {o.left}
                <span style={{ flex: 1 }}>{o.label}</span>
                {isSel && (
                  <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden style={{ flex: "none", color: "var(--brand-primary)" }}>
                    <path fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
                  </svg>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={wrapRef} style={{ position: "relative", ...style }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => { if (open) close(); else openList(); }}
        onKeyDown={onTriggerKeyDown}
        style={trigger}
      >
        <span style={{ ...triggerLabel, color: selected ? "var(--ink)" : "var(--ink-3)" }}>
          {selected?.left}
          {selected ? selected.label : (placeholder ?? "Select…")}
        </span>
        <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden
          style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s", color: "var(--ink-3)" }}>
          <path fill="none" stroke="currentColor" strokeWidth="1.5" d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {list && createPortal(list, document.body)}
    </div>
  );
}

const trigger: CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, textAlign: "left", cursor: "pointer",
};
const triggerLabel: CSSProperties = {
  flex: 1, display: "flex", alignItems: "center", gap: 8,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
const panel: CSSProperties = {
  zIndex: 1000,
  display: "flex", flexDirection: "column",
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)", boxShadow: "var(--shadow-2)",
  overflow: "hidden",
};
const listStyle: CSSProperties = { padding: 4, overflowY: "auto", minHeight: 0 };
const groupHead: CSSProperties = {
  padding: "6px 10px 3px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
  textTransform: "uppercase", color: "var(--ink-4)",
};
const option: CSSProperties = {
  display: "flex", gap: 10, alignItems: "center", width: "100%", textAlign: "left",
  padding: "8px 10px", borderRadius: 6, border: "none", cursor: "pointer",
  fontSize: 13, color: "var(--ink)",
};
